// frontend/src/components/MetaCubeOraclePlaylist.jsx
import React, { useState, useEffect, useRef } from "react";
import useMetaCubeOracleVisualizer from "../hooks/useMetaCubeOracleVisualizer";

// Import des 5 versions React
import MetaCubeOracleV1 from "./MetaCubeOracleV1";
import MetaCubeOracleV2 from "./MetaCubeOracleV2";
import MetaCubeOracleV3 from "./MetaCubeOracleV3";
import MetaCubeOracleV4 from "./MetaCubeOracleV4";
import MetaCubeOracleV5 from "./MetaCubeOracleV5";

const PLAYLIST_INTERVAL = 15000; // 15s

const VISUALIZERS = [
  { name: "MetaCube Oracle V1", component: MetaCubeOracleV1 },
  { name: "MetaCube Oracle V2", component: MetaCubeOracleV2 },
  { name: "MetaCube Oracle V3", component: MetaCubeOracleV3 },
  { name: "MetaCube Oracle V4", component: MetaCubeOracleV4 },
  { name: "MetaCube Oracle V5", component: MetaCubeOracleV5 },
];

export default function MetaCubeOraclePlaylist() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef(null);

  // --- Entropie (UI depuis API + dynamique locale) ---
  // Le hook de mesure n'est plus consommé ici : la playlist ne mesure rien.
  useMetaCubeOracleVisualizer({ playlistInterval: PLAYLIST_INTERVAL });
  // ⚠️ AFFICHAGE MÉTRIQUE RETIRÉ le 2026-07-29.
  //
  // Cette playlist lisait `data.shannon_entropy` de
  // `GET /api/geometry/metacube_oracle/entropy` — une entropie de Shannon
  // calculée sur un BLOB D'OCTETS (256 classes), et non sur une scène. C'était
  // une TROISIÈME définition de la grandeur, incommensurable avec les
  // observables de `geometry/observables.py` (histogrammes à 32 classes, en
  // bits par échantillon).
  //
  // Pire, elle l'ADDITIONNAIT à `dynEntropy.shannon`, qui mesure une scène :
  // une somme de deux grandeurs sans dimension commune, exactement ce que
  // l'unification dimensionnelle du chantier a éliminé partout ailleurs.
  //
  // La playlist n'a aucun rôle de mesure — c'est une navigation entre versions.
  // Elle n'en affiche donc plus. Aucune formule n'a été inventée pour la
  // remplacer : quand une valeur n'a pas de définition commune, on la retire
  // plutôt que d'en fabriquer une.

  // --- Playlist controls ---
  const startPlaylist = () => {
    if (isRunning) return;
    setIsRunning(true);
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % VISUALIZERS.length);
    }, PLAYLIST_INTERVAL);
  };

  const stopPlaylist = () => {
    setIsRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const nextVersion = () => {
    setCurrentIndex((prev) => (prev + 1) % VISUALIZERS.length);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // --- Rendu du visualizer courant ---
  const CurrentVisualizer = VISUALIZERS[currentIndex].component;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <h2>
        {isRunning ? "🎵" : "⏸️"} {VISUALIZERS[currentIndex].name} (
        {currentIndex + 1}/{VISUALIZERS.length})
      </h2>

      {/* Jauges retirées — voir le commentaire plus haut. Le « Score Total »
          y additionnait une entropie d'octets et une entropie de scène. */}

      {/* Zone du visualizer */}
      <div
        style={{
          width: "640px",
          height: "480px",
          borderRadius: "10px",
          background: "#111",
          overflow: "hidden",
        }}
      >
        <CurrentVisualizer />
      </div>

      {/* Boutons playlist */}
      <div style={{ display: "flex", gap: 16 }}>
        <button
          onClick={startPlaylist}
          disabled={isRunning}
          style={{ padding: "10px 18px" }}
        >
          ▶️ Démarrer Playlist
        </button>
        <button onClick={stopPlaylist} style={{ padding: "10px 18px" }}>
          ⏹️ Arrêter Playlist
        </button>
        <button onClick={nextVersion} style={{ padding: "10px 18px" }}>
          ⏭️ Version Suivante
        </button>
      </div>
    </div>
  );
}
